import { ButtonV2 } from '@cimplico/components';
import { ArrowRightIcon, PlusIcon } from '@heroicons/react/20/solid';
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { ReactNode } from 'react';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';

type EmptyStateNewProps = {
    onClick?: () => void;
    title: string;
    helpText?: string;
    helpLinkText?: string;
    helpLink?: string;
    disableActionButton?: boolean;
    disableActionButtonHint?: string;
    actionText?: string;
    actionButtonType?: 'primary' | 'danger' | 'secondary' | 'hollow';
    navigateTo?: string;
    titleSize?: 'small' | 'base';
    icon?: ReactNode;
    showButtonIcon?: boolean;
    primaryAction?: ReactNode;
};

function EmptyStateNew({
    onClick,
    title,
    helpLink,
    helpText,
    actionText,
    actionButtonType = 'primary',
    navigateTo,
    titleSize = 'base',
    icon,
    disableActionButton = false,
    disableActionButtonHint,
    showButtonIcon = true,
    primaryAction,
    helpLinkText = 'Learn more',
}: EmptyStateNewProps): JSX.Element {
    const navigate = useNavigate();
    const activeSubscription = useIsActiveSubscription();
    const { shortId } = useFirmContext();

    function handleAction(): void {
        if (onClick) {
            onClick();
        } else if (navigateTo) {
            navigate(`/${shortId}${navigateTo}`);
        }
    }

    return (
        <div className="text-center">
            <div className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true">
                {icon || <RectangleStackIcon />}
            </div>
            <h3 className={`mt-2 font-semibold text-gray-900 dark:text-gray-200 ${titleSize === 'base' ? 'text-lg' : 'text-sm'}`}>
                {title}
            </h3>
            {helpText && <p className="mt-1 text-sm text-gray-500">{helpText}</p>}
            {helpLink && (
                <a
                    className="text-indigo-600 dark:text-indigo-400 text-sm flex justify-center items-center gap-1 mt-1 w-fit mx-auto"
                    href={helpLink}
                    target="_blank"
                    rel="noreferrer"
                >
                    {helpLinkText} <ArrowRightIcon className="h-4 w-4" />
                </a>
            )}
            {(onClick || navigateTo) && (
                <div className="mt-6 text-white w-fit mx-auto">
                    <ButtonV2
                        label={actionText}
                        type={actionButtonType}
                        size="sm"
                        onClick={handleAction}
                        isDisabled={!activeSubscription || disableActionButton}
                        leftIcon={showButtonIcon ? <PlusIcon /> : undefined}
                        disabledHint={disableActionButtonHint}
                    />
                </div>
            )}
            {primaryAction && <div className="mt-6 text-white w-fit mx-auto">{primaryAction}</div>}
        </div>
    );
}

export default EmptyStateNew;
